class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this._lastSyncAt = 0;

    this._onPageShow = (e) => {
      const nav = performance.getEntriesByType?.('navigation')?.[0];
      const wasBFCache = e.persisted || nav?.type === 'back_forward';
      if (wasBFCache) {
        this.refreshCartIconBubble();
        this.syncCartDrawer('bfcache');
      }
    };
    window.addEventListener('pageshow', this._onPageShow);

    const schedule = window.requestIdleCallback || ((fn) => setTimeout(fn, 0));
    schedule(() => {
      this.refreshCartIconBubble();
      this.syncCartDrawer('product-load');
    });
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this._onPageShow);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async syncCartDrawer(reason = 'manual') {
    try {
      const res = await fetch(`${routes.cart_url}?section_id=cart-drawer&ts=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-store', Pragma: 'no-cache' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Scope to this component so we don't touch other drawers
      const incomingHeader = doc.querySelector('.drawer__header');
      const incomingItems = doc.querySelector('cart-drawer-items');
      const incomingFooter = doc.querySelector('.drawer__footer');

      const header = this.querySelector('.drawer__header');
      const items = this.querySelector('cart-drawer-items');
      const footer = this.querySelector('.drawer__footer');

      if (incomingHeader && header) header.innerHTML = incomingHeader.innerHTML;
      if (incomingItems && items) items.innerHTML = incomingItems.innerHTML;
      if (incomingFooter && footer) footer.innerHTML = incomingFooter.innerHTML;

      // Authoritative empty state from /cart.js (no-store)
      let isEmpty = false;
      try {
        const cartRes = await fetch('/cart.js', { cache: 'no-store' });
        if (cartRes.ok) {
          const cart = await cartRes.json();
          isEmpty = (cart.item_count ?? 0) === 0;
        }
      } catch {}

      this.classList.toggle('is-empty', isEmpty);
      if (items) items.classList.toggle('is-empty', isEmpty);
      if (!isEmpty) this.querySelector('.drawer__inner-empty')?.remove();

      // Rebind overlay inside this component
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.removeEventListener('click', this.close);
        overlay.addEventListener('click', this.close.bind(this));
      }
      this._lastSyncAt = Date.now();
      console.debug('[cart] drawer refreshed:', reason, 'empty:', isEmpty);
    } catch (err) {
      console.warn('[cart] drawer refresh failed:', err);
    }
  }

  async refreshCartIconBubble() {
    try {
      const res = await fetch(`${routes.cart_url}?section_id=cart-icon-bubble&ts=${Date.now()}`, { cache: 'no-store' });
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const src = doc.querySelector('#shopify-section-cart-icon-bubble');
      const tgt = document.querySelector('#cart-icon-bubble');
      if (src && tgt) tgt.innerHTML = src.innerHTML;
    } catch (e) {
      console.warn('refreshCartIconBubble failed', e);
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // If we haven’t synced in 3s, do a quick sync first.
    const now = Date.now();
    if (!this._lastSyncAt || now - this._lastSyncAt > 3000) {
      // Fire and forget; the transition can still start
      this.syncCartDrawer('pre-open').catch(() => {});
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const chatIcon = document.getElementById('gladlyChatDragHandle');
    if (chatIcon) chatIcon.style.display = 'none';

    // reliable transition
    this.classList.add('animate');
    this.offsetWidth; // reflow
    requestAnimationFrame(() => this.classList.add('active'));

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    const chatIcon = document.getElementById('gladlyChatDragHandle');
    if (chatIcon) chatIcon.style.display = 'block';
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
